import React, { useEffect, useState } from 'react';
import styles from './index.module.sass';
import superagent from 'superagent';

/**
 * Categorías disponibles en el formulario de carga de videos.
 *
 * Los valores deben coincidir con el campo `categoria` que devuelve
 * el endpoint GET getVideos del webservice regional.
 *
 * La comparación se normaliza eliminando espacios laterales y
 * convirtiendo el texto a minúsculas, pero el nombre de cada categoría
 * debe conservar la misma redacción utilizada por el servicio.
 *
 * Nota:
 * El objeto completo de la categoría, incluyendo `categoriaES`,
 * `categoriaEN` y `videos`, se obtiene posteriormente desde getVideos.
 */

/**
const categorias = [
  'Creating smiles',
  'Brochure of the Month',
  'Events',
  'Product Video',
  'Reels',
  'Tutorials',
  'Corporate Video',
];
 */

/**
 * Componente UploadVideos
 *
 * Permite registrar videos en el administrador de la región US.
 *
 * El componente integra dos contratos distintos del mismo webservice:
 *
 * GET getVideos:
 * - Devuelve las categorías disponibles.
 * - Cada categoría incluye `categoria`, `categoriaES`, `categoriaEN`
 *   y un arreglo `videos`.
 * - Cada video utiliza la propiedad `orden` para indicar su posición.
 *
 * POST PostAgregarVideo:
 * - Espera los campos `categoriaEs`, `categoriaEn`.
 * - Espera `tituloEs`, `tituloEn`, `linkEs` y `linkEn`.
 * - Utiliza `secuencia` en lugar de `orden`.
 * - Requiere un token válido para identificar al usuario que registra
 *   el video.
 *
 * Flujo principal:
 * 1. El usuario selecciona una categoría del formulario.
 * 2. Se consulta GET getVideos.
 * 3. Se localiza el objeto completo de la categoría seleccionada.
 * 4. Se obtiene el mayor valor de `video.orden`.
 * 5. Se calcula el siguiente orden disponible: orden máximo + 1.
 * 6. El resultado se guarda internamente en `formData.secuencia`.
 * 7. Al enviar el formulario, los datos se adaptan al contrato de
 *    POST PostAgregarVideo.
 * 8. El mismo título y enlace se envían temporalmente para español
 *    e inglés, porque el formulario captura una sola versión.
 *
 * Traducciones:
 * Los textos visuales se obtienen desde `Lang.langUploadVideos`.
 */
function UploadVideos({ 
    Lang = {},
    webserviceURL,
    setSectionName,
    setOptionSelected, }) {

  const { langUploadVideos = {} } = Lang;

  useEffect(() => {
    setSectionName(langUploadVideos.title || 'Upload videos');
    setOptionSelected('uploadVideos');
    }, [
    langUploadVideos.title,
    setSectionName,
    setOptionSelected,
    ]);

  /**
   * Datos visibles y editables del formulario.
   *
   * `secuencia` es el nombre interno utilizado por React y por el POST.
   * Para calcularla, se utiliza la propiedad `orden` devuelta por getVideos.
   */
  const [formData, setFormData] = useState({
    categoria: '',
    tituloEs: '',
    tituloEn: '',
    linkEs: '',
    linkEn: '',
    secuencia: '',
  });

  /**
   * Conserva el objeto completo de la categoría devuelto por getVideos.
   *
   * Ejemplo:
   * {
   *   categoria: 'Reels',
   *   categoriaES: 'Reels',
   *   categoriaEN: 'Reels',
   *   videos: [...]
   * }
   *
   * Esta información es necesaria para construir el JSON bilingüe
   * requerido por PostAgregarVideo.
   */
  const [categoriaServicio, setCategoriaServicio] = useState(null);

  const [categoriasDisponibles, setCategoriasDisponibles] = useState([]);

  const [loading, setLoading] = useState(false);

  const [loadingCategorias, setLoadingCategorias] = useState(false);

  // Loading separado para evitar que el botón diga "Guardando"
  // mientras solo se está calculando la secuencia.
  const [loadingSecuencia, setLoadingSecuencia] = useState(false);

  // Mensaje visible para éxito, error o validaciones del formulario.
  const [mensaje, setMensaje] = useState('');


  useEffect(() => {
    if (!webserviceURL) {
      return;
    }

    const url = `${webserviceURL}/getVideos`;

    setLoadingCategorias(true);

    superagent
      .get(url)
      .set('Content-Type', 'application/json')
      .set('token', localStorage.getItem('TMBtoken'))
      .auth(localStorage.getItem('TMBtoken'), { type: 'bearer' })
      .then((res) => {
        const categoriasResponse = Array.isArray(res.body)
          ? res.body
          : res.body?.data || [];

        setCategoriasDisponibles(categoriasResponse);
      })
      .catch(() => {
        setCategoriasDisponibles([]);
        setMensaje('No se pudieron cargar las categorías');
      })
      .finally(() => {
        setLoadingCategorias(false);
      });
  }, [webserviceURL]);



  /**
   * Actualiza los campos editables del formulario.
   *
   * Este handler se usa para título y link.
   * La categoría usa un handler separado porque al cambiarla
   * también debe calcularse automáticamente la secuencia.
   */
  const handleChange = (event) => {
    const { name, value } = event.target;

       //console.log('Campo modificado:', name, value);

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  /**
   * Consulta GET getVideos y calcula la siguiente secuencia disponible
   * para la categoría seleccionada.
   *
   * Estructura esperada:
   * [
   *   {
   *     categoria: 'Reels',
   *     categoriaES: 'Reels',
   *     categoriaEN: 'Reels',
   *     videos: [
   *       {
   *         enlace: 'abc123',
   *         texto: 'Video de ejemplo',
   *         enlaceES: 'abc123',
   *         textoES: 'Video de ejemplo',
   *         enlaceEN: 'abc123',
   *         textoEN: 'Example video',
   *         orden: 1
   *       }
   *     ]
   *   }
   * ]
   *
   * Proceso:
   * 1. Busca la categoría usando el campo `categoria`.
   * 2. Conserva el objeto completo en `categoriaServicio`.
   * 3. Extrae los valores numéricos de `video.orden`.
   * 4. Obtiene el mayor orden existente.
   * 5. Calcula el siguiente valor sumando uno.
   * 6. Guarda ese resultado en `formData.secuencia`.
   *
   * Aunque el GET utiliza el nombre `orden`, el estado y el POST utilizan
   * el nombre `secuencia`.
   */
  const obtenerSecuenciaCategoria = (nombreCategoria) => {
    setLoadingSecuencia(true);
    setMensaje('');

    const url = `${webserviceURL}/getVideos`;

    superagent
      .get(url)
      .set('Content-Type', 'application/json')
      .set('token', localStorage.getItem('TMBtoken'))
      .auth(localStorage.getItem('TMBtoken'), { type: 'bearer' })
      .then((res) => {
        console.log('Respuesta consulta videos:', res.body);

        const categoriasResponse = Array.isArray(res.body)
          ? res.body
          : res.body?.data || [];
        
        /**
        * Normaliza los nombres de categoría para evitar diferencias por
        * mayúsculas, minúsculas o espacios laterales entre ambientes.
        */
        const normalizarTexto = (texto = '') =>
        texto.trim().toLowerCase();

        const categoriaEncontrada = categoriasResponse.find(
            (item) =>
                normalizarTexto(item.categoria) ===
                normalizarTexto(nombreCategoria)
        );

        console.log('Categoría encontrada:', categoriaEncontrada);

        if (!categoriaEncontrada) {
        setCategoriaServicio(null);
        setMensaje('La categoría seleccionada no existe en el servicio');

        setFormData((prevData) => ({
            ...prevData,
            secuencia: '',
        }));

        return;
        }

        setCategoriaServicio(categoriaEncontrada);

        const videos = categoriaEncontrada?.videos || [];

        const ordenes = videos
          .map((video) => Number(video.orden))
          .filter((orden) => Number.isFinite(orden));

        const ordenActual  =
          ordenes.length > 0 ? Math.max(...ordenes) : 0;

        const siguienteOrden = ordenActual + 1;

        console.log('Secuencia actual:', ordenActual);
        console.log('Siguiente secuencia:', siguienteOrden);

        setFormData((prevData) => {
          /**
           * Protección contra cambios rápidos:
           *
           * Si el usuario cambia de categoría antes de que responda el webservice,
           * evitamos escribir una secuencia que pertenezca a una categoría anterior.
           */
            if (prevData.categoria !== nombreCategoria) {
                return prevData;
            }

          return {
            ...prevData,
            secuencia: siguienteOrden,
          };
        });
      })
      .catch((err) => {
        console.error('Error al consultar secuencia:', err);
        setMensaje('No se pudo generar la secuencia automáticamente');

        setFormData((prevData) => ({
          ...prevData,
          secuencia: '',
        }));
      })
      .finally(() => {
        setLoadingSecuencia(false);
      });
  };

    /**
     * Maneja la selección de una categoría.
     *
     * Al cambiar la opción:
     * 1. Elimina el objeto de categoría previamente almacenado.
     * 2. Limpia mensajes y la secuencia anterior.
     * 3. Actualiza el nombre seleccionado en `formData.categoria`.
     * 4. Consulta getVideos para obtener el objeto regional completo.
     * 5. Calcula automáticamente la siguiente secuencia.
     */
    const handleCategoriaChange = (event) => {
        const { value } = event.target;

        setCategoriaServicio(null);
        setMensaje('');

        setFormData((prevData) => ({
            ...prevData,
            categoria: value,
            secuencia: '',
        }));

        if (value) {
            obtenerSecuenciaCategoria(value);
        }
    };

  /**
   * Valida y envía el nuevo video a POST PostAgregarVideo.
   *
   * El endpoint requiere un contrato diferente al utilizado por getVideos.
   *
   * Conversión realizada:
   * - `categoriaServicio.categoriaES` → `categoriaEs`
   * - `categoriaServicio.categoriaEN` → `categoriaEn`
   * - `formData.titulo` → `tituloEs` y `tituloEn`
   * - `formData.link` → `linkEs` y `linkEn`
   * - `formData.secuencia` → `secuencia`
   *
   * El mismo título y enlace se envían para ambos idiomas porque
   * actualmente el formulario solo captura una versión de cada dato.
   *
   * El campo `idioma` controla el idioma de los mensajes generados por
   * el webservice:
   * - "0": español
   * - "1": inglés
   *
   * Autenticación:
   * El servicio requiere el token guardado en `localStorage.TMBtoken`,
   * enviado tanto en el encabezado `token` como mediante Bearer.
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.categoria || !formData.tituloEs || !formData.tituloEn || !formData.linkEs || !formData.linkEn) {
        setMensaje('Categoría, títulos y links son obligatorios');
        return;
    }

    if (!formData.secuencia) {
        setMensaje('No se pudo generar la secuencia. Selecciona nuevamente la categoría');
        return;
    }

    if (!categoriaServicio) {
        setMensaje('No se pudo obtener la información completa de la categoría');
        return;
    }

    setLoading(true);
    setMensaje('');

    /**
    * Adaptación del contrato de lectura al contrato de escritura.
    *
    * Importante:
    * El GET devuelve `categoriaES` y `categoriaEN`, mientras que el POST
    * espera `categoriaEs` y `categoriaEn`.
    *
    * La capitalización de las propiedades debe conservarse exactamente
    * como la espera cada endpoint.
    */
    const json = {
      categoriaEs: categoriaServicio.categoriaES,
      categoriaEn: categoriaServicio.categoriaEN,
      tituloEs: formData.tituloEs.trim(),
      tituloEn: formData.tituloEn.trim(),
      linkEs: formData.linkEs.trim(),
      linkEn: formData.linkEn.trim(),
      secuencia: String(formData.secuencia),
      idioma: '0',
    };

    console.log('JSON enviado a PostAgregarVideo:', json);

    const url = `${webserviceURL}/PostAgregarVideo`;

    superagent
      .post(url)
      .set('Content-Type', 'application/json')
      .send(json)
      .set('token', localStorage.getItem('TMBtoken'))
      .auth(localStorage.getItem('TMBtoken'), { type: 'bearer' })
      .then((res) => {
        const { body = {} } = res;

        const {
          typeMessage = '',
          message = '',
          messageCode = '',
        } = body;

        if (typeMessage === 'success' || Number(messageCode) === 200) {
          setMensaje('Video agregado correctamente');
          setCategoriaServicio(null);

          setFormData({
            categoria: '',
            tituloEs: '',
            tituloEn: '',
            linkEs: '',
            linkEn: '',
            secuencia: '',
          });
        } else {
          setMensaje(message || 'No se pudo agregar el video');
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al agregar video:', err);
        setMensaje('Ocurrió un error al agregar el video');
        setLoading(false);
      });
  };

    /**
    * Indica si el formulario contiene todos los datos necesarios
    * para habilitar el botón Guardar.
    *
    * Requisitos:
    * - categoría seleccionada;
    * - título;
    * - enlace;
    * - secuencia generada automáticamente.
    *
    * El botón también permanece deshabilitado mientras se consulta
    * la secuencia o se guarda el registro.
    */
    const formularioCompleto =
    formData.categoria &&
    formData.tituloEs.trim() &&
    formData.tituloEn.trim() &&
    formData.linkEs.trim() &&
    formData.linkEn.trim() &&
    formData.secuencia &&
    categoriaServicio;


  return (
    <div className={styles.container}>
      <h2>{langUploadVideos.title || 'Upload videos'}</h2>

      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.label}>
          {langUploadVideos.category || 'Category'}
        <select
          name="categoria"
          value={formData.categoria}
          onChange={handleCategoriaChange}
          className={styles.input}
          disabled={loadingCategorias}
        >
          <option value="">
            {loadingCategorias
              ? langUploadVideos.loadingCategories || 'Loading categories...'
              : langUploadVideos.selectCategory || 'Select a category'}
          </option>

          {categoriasDisponibles.map((categoria) => {
            const categoriaEn =
              categoria.categoriaEN || categoria.categoria || '';

            const categoriaEs = categoria.categoriaES || '';

            const textoVisible =
              categoriaEn && categoriaEs
                ? `${categoriaEn} — ${categoriaEs}`
                : categoriaEn || categoriaEs;

            return (
              <option
                key={categoria.categoria}
                value={categoria.categoria}
              >
                {textoVisible}
              </option>
            );
          })}
        </select>
        </label>

        <label className={styles.label}>
          {langUploadVideos.videoTitleEs || 'Título en español'}
          <input
            type="text"
            name="tituloEs"
            value={formData.tituloEs}
            onChange={handleChange}
            className={styles.input}
            placeholder="Experiencia XV Estrellas Adriana"
          />
        </label>

        <label className={styles.label}>
          {langUploadVideos.videoTitleEn || 'Title in English'}
          <input
            type="text"
            name="tituloEn"
            value={formData.tituloEn}
            onChange={handleChange}
            className={styles.input}
            placeholder="Adriana XV Stars Experience"
          />
        </label>

        <label className={styles.label}>
          {langUploadVideos.linkEs || 'Enlace en español'}
          <input
            type="text"
            name="linkEs"
            value={formData.linkEs}
            onChange={handleChange}
            className={styles.input}
            placeholder="21Ffd3aESPañol"
          />
        </label>

        <label className={styles.label}>
          {langUploadVideos.linkEn || 'Link in English'}
          <input
            type="text"
            name="linkEn"
            value={formData.linkEn}
            onChange={handleChange}
            className={styles.input}
            placeholder="614qEnjzIngles"
          />
        </label>

        <label className={styles.label}>
          {langUploadVideos.sequence || 'Sequence'}
          {/*
            El usuario no puede modificar manualmente la secuencia.

            El valor se calcula a partir del mayor `orden` devuelto por getVideos,
            pero se conserva en `formData.secuencia` porque ese es el nombre
            esperado por PostAgregarVideo.
          */}
          <input
            type="number"
            name="secuencia"
            value={formData.secuencia}
            readOnly
            className={styles.input}
            placeholder={
                loadingSecuencia
                ? langUploadVideos.generatingSequence || 'Generating sequence...'
                : langUploadVideos.automaticSequence || 'Generated automatically'
            }
          />
        </label>

        <button
          type="submit"
          className={`${styles.button} ${
            formularioCompleto ? styles.buttonActive : styles.buttonDisabled
          }`}
          disabled={!formularioCompleto || loading || loadingSecuencia}
        >
          {loading
            ? langUploadVideos.saving || 'Saving...'
            : loadingSecuencia
                ? langUploadVideos.generatingSequence || 'Generating sequence...'
                : langUploadVideos.save || 'Save'}
        </button>
      </form>

      {mensaje && <p className={styles.message}>{mensaje}</p>}
    </div>
  );
}

export default UploadVideos;